import {useCallback, useMemo, useRef, useState} from 'react';
// eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
import {unstable_batchedUpdates} from 'react-dom';
import {Edge, Node} from 'reactflow';

import {isFlowchartFeatureEnabled} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/FlowchartFeaturesContext';
import {ProcessFlowchartEditorModeFeatureType} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {
  ShouldAddEdgesForNewNodeFn,
  UseFlowchartStateParams,
  UseFlowchartStateReturn,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';
import {ElkEdgeData} from 'ui-library/reactflow/edges/edgeOptions';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {useFlowchartSidepanelManagerContext} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';
import {SidepanelType} from 'ui-library/reactflow/sidepanels/sidepanel-manager/useFlowchartSidepanelManager';

import {FlowchartEdge} from '../processFlowchartConfigEditorConstants';
import {FlowchartNode} from '../processFlowchartConfigEditorNodeTypes';
import {
  FlowchartNodeChange,
  FlowchartNodeChangeType,
  FlowchartOptions,
} from '../processFlowchartConfigEditorUtils';
import {useTransitionsContext} from '../types/transition/TransitionsContext';
import {shouldAddEdgesForNewNode} from './shouldAddEdgesForNewNode';
import {useAddNodeFromRightHandle} from './useAddNodeFromRightHandle';
import {useApplyFlowchartChanges} from './useApplyFlowchartChanges';
import {useDeleteKeyHandler} from './useDeleteKeyHandler';
import {useInteractiveFlowchartBuilder} from './useInteractiveFlowchartBuilder';
import {useMultiSelectAnalytics} from './useMultiSelectAnalytics';
import {useNodeDeletion} from './useNodeDeletion';
import {useNodeDimensions} from './useNodeDimensions';
import {useNodeInternalsRefresh} from './useNodeInternalsRefresh';
import {useOnConnect} from './useOnConnect';
import {usePlaceholderNodeSidepanel} from './usePlaceholderNodeSidepanel';
import {useReactFlowChangesOnly} from './useReactFlowChangesOnly';
import {useRightSidepanelNodeSelection} from './useRightSidepanelNodeSelection';
import {useSidepanelDeselection} from './useSidepanelDeselection';

export {extractStaticData, extractStaticEdgeData} from './extractStaticData';
export {getEmptyFlowchartChanges} from './flowchartChangesUtils';
export {applyFlowchartChangesCore} from './applyFlowchartChangesCore';

/**
 * Contains the core flowchart state management logic for the horizontal flowchart.
 *
 * Main features:
 * - Apply changes to the flowchart and/or metadata
 * - Handle right sidepanel state management
 * - Handle reactflow actions related to nodes + edges
 *
 * Core concepts:
 * - Minimal flowchart representations to actively track:
 *      We should rarely, if ever, need to mutate or interact with the interactive
 *      nodes/edges directly, which gets sent to reactflow for rendering. Instead,
 *      we change the static flowchart, metadata, and interactive state trackers as
 *      needed, and the interactive flowchart is rebuilt from those sources of truth
 *      automatically.
 * - All change management is funneled through the applyFlowchartChanges function:
 *      This allows for centralizing side effects and autohealing logic related to
 *      flowchart changes.
 */
const useFlowchartState = (params: UseFlowchartStateParams): UseFlowchartStateReturn => {
  const {
    staticFlowchart,
    flowchartConfigMetadata,
    flowchartConfigId,
    validationErrors,
    setFlowchartAndMetadata,
    addNodeParams,
    flowchartFeatures,
    reactFlowInstance,
    reactFlowWrapper,
    leftSidepanelRef,
    rightSidepanelRef,
    rightPanelSize,
  } = params;

  const {flowchartTypeName} = flowchartConfigMetadata;
  const plugin = PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(flowchartTypeName);
  const {ptdvPairToTransitionIdIndex} = useTransitionsContext();
  const shouldIncrementNodeNames = isFlowchartFeatureEnabled(
    flowchartFeatures,
    ProcessFlowchartEditorModeFeatureType.SHOW_NODE_NAME_INCREMENT
  );

  const locked = ReactFlowLockedContext.useContext() ?? false;
  const allowStartEndNodeDeletion = isFlowchartFeatureEnabled(
    flowchartFeatures,
    ProcessFlowchartEditorModeFeatureType.ALLOW_START_END_NODE_DELETION
  );

  const {getMeasuredDimensionsFromStore} = useNodeDimensions();

  const [interactiveNodes, setInteractiveNodes] = useState<Array<FlowchartNode>>([]);
  const [interactiveEdges, setInteractiveEdges] = useState<Array<FlowchartEdge>>([]);
  const [nodePositions, setNodePositions] = useState<Record<string, {x: number; y: number}>>({});

  // Ref to track current node positions for use in rebuilds during drag operations,
  // preserving drag positions without triggering full flowchart rebuilds.
  const currentNodePositionsRef = useRef<Record<string, {x: number; y: number}>>({});

  const interactiveNodesRef = useRef<Array<FlowchartNode>>(interactiveNodes);
  interactiveNodesRef.current = interactiveNodes;

  const sidepanelManager = useFlowchartSidepanelManagerContext();
  const sidepanelState = sidepanelManager.getEffectiveState();
  const {openSidepanelType} = sidepanelState;
  const sidepanelStateRef = useRef(sidepanelState);
  sidepanelStateRef.current = sidepanelState;
  const isLeftSidepanelOpen = useMemo(
    () => openSidepanelType === SidepanelType.FLOWCHART_SETTINGS,
    [openSidepanelType]
  );

  // Tracks nodes that need their ReactFlow handle positions refreshed after a REPLACE
  // change (e.g., field reordering). Populated by useApplyFlowchartChanges, consumed by
  // useNodeInternalsRefresh.
  const nodeIdsToRefreshRef = useRef<Set<string>>(new Set());

  const applyFlowchartChanges = useApplyFlowchartChanges({
    setFlowchartAndMetadata,
    setNodePositions,
    nodeIdsToRefreshRef,
    plugin,
    ptdvPairToTransitionIdIndex,
    shouldIncrementNodeNames,
    locked,
    flowchartTypeName,
    flowchartConfigId,
  });

  const {openSidepanelForNode, openSidepanelForNodeWithField} = useRightSidepanelNodeSelection({
    applyFlowchartChanges,
    sidepanelManager,
    setInteractiveNodes,
    interactiveNodesRef,
    platformFlowchartFeatures: params.flowchartEditorConfigurator.platformFlowchartFeatures,
    flowchartTypeName,
    reactFlowInstance,
    reactFlowWrapper,
    leftSidepanelRef,
    rightSidepanelRef,
    nodePositions,
    getMeasuredDimensionsFromStore,
  });

  const {getAddNodeOptionsForRightHandle, placeholderNodeIdBeingAdded, setPlaceholderNodeBeingAdded} =
    useAddNodeFromRightHandle({
      addNodeParams,
      applyFlowchartChanges,
      flowchartTypeName,
      openSidepanelForNode,
      openSidepanelForNodeWithField,
      sidepanelManager,
    });

  const {onReactFlowNodeChangesOnly, onReactFlowEdgeChangesOnly} = useReactFlowChangesOnly({
    applyFlowchartChanges,
    setInteractiveNodes,
    setNodePositions,
    currentNodePositionsRef,
  });

  const {deleteNodesWithConfirmation, onNodeDelete} = useNodeDeletion({
    locked,
    allowStartEndNodeDeletion,
    plugin,
    flowchartConfigMetadata,
    staticNodesData: staticFlowchart.nodesStaticData,
    flowchartTypeName,
    reactFlowInstance,
    sidepanelManager,
    onReactFlowNodeChangesOnly,
    onReactFlowEdgeChangesOnly,
  });

  const interactiveFlowchartOptions: FlowchartOptions = useMemo(
    () => ({
      onNodeHeaderClick: openSidepanelForNode,
      onNodeDelete,
      onFieldClick: openSidepanelForNodeWithField,
      onUpdateNodeData: (nodeChangeGenerator, saveImmediately, skipSettingDirtyState) =>
        applyFlowchartChanges(
          (flowchartConfig) => ({
            nodeChanges: nodeChangeGenerator(flowchartConfig),
          }),
          saveImmediately,
          skipSettingDirtyState
        ),
      getAddNodeOptionsForRightHandle,
      validationErrors,
      existingNodeNames: staticFlowchart.nodesStaticData.map((node) => node.nodeName),
      platformFlowchartFeatures: params.flowchartEditorConfigurator.platformFlowchartFeatures,
    }),
    [
      openSidepanelForNode,
      onNodeDelete,
      openSidepanelForNodeWithField,
      getAddNodeOptionsForRightHandle,
      validationErrors,
      applyFlowchartChanges,
      staticFlowchart.nodesStaticData,
      params.flowchartEditorConfigurator.platformFlowchartFeatures,
    ]
  );

  const {setElkEdgeDataByEdgeId} = useInteractiveFlowchartBuilder({
    staticFlowchart,
    flowchartConfigMetadata,
    nodePositions,
    currentNodePositionsRef,
    interactiveFlowchartOptions,
    validationErrors,
    setInteractiveNodes,
    setInteractiveEdges,
  });

  useNodeInternalsRefresh(interactiveNodes, nodeIdsToRefreshRef);

  usePlaceholderNodeSidepanel({
    placeholderNodeIdBeingAdded,
    setPlaceholderNodeBeingAdded,
    interactiveNodes,
    applyFlowchartChanges,
    openSidepanelForNode,
  });

  useSidepanelDeselection({applyFlowchartChanges});

  const shouldAddEdgesForNewNodeCallback = useCallback<ShouldAddEdgesForNewNodeFn>(
    (edges, newNodeStaticData) =>
      shouldAddEdgesForNewNode({
        edges,
        newNodeStaticData,
        staticFlowchart,
        transitions: flowchartConfigMetadata.transitions,
        flowchartTypeName,
      }),
    [staticFlowchart, flowchartConfigMetadata.transitions, flowchartTypeName]
  );

  const onConnect = useOnConnect({
    applyFlowchartChanges,
    staticFlowchart,
    transitions: flowchartConfigMetadata.transitions,
    flowchartTypeName,
  });

  const setNodesAndEdgesViaChangesForLayout = useCallback(
    (newNodes: Array<Node>, newEdges: Array<Edge>): void => {
      // Use unstable_batchedUpdates to ensure both state updates happen in a single render cycle.
      // NOTE: This is not necessary in React 18. Without this, updating nodes and edges separately
      // can cause an interstitial rebuild to fire between the two updates.
      // unstable_batchedUpdates is actually quite stable: https://stackoverflow.com/a/48610973
      unstable_batchedUpdates(() => {
        const nodeChanges: Array<FlowchartNodeChange> = newNodes.map((node) => ({
          type: FlowchartNodeChangeType.POSITION,
          id: node.id,
          position: node.position,
        }));
        onReactFlowNodeChangesOnly(nodeChanges);

        const newElkEdgeDataByEdgeId = newEdges.reduce(
          (acc, e) => {
            acc[e.id] = e.data.elkEdgeData;
            return acc;
          },
          {} as Record<string, ElkEdgeData>
        );
        setElkEdgeDataByEdgeId(newElkEdgeDataByEdgeId);
      });
    },
    [setElkEdgeDataByEdgeId, onReactFlowNodeChangesOnly]
  );

  useDeleteKeyHandler({
    locked,
    reactFlowWrapper,
    leftSidepanelRef,
    rightSidepanelRef,
    sidepanelStateRef,
    rightPanelSize,
    interactiveNodes,
    interactiveEdges,
    flowchartTypeName,
    onReactFlowEdgeChangesOnly,
    deleteNodesWithConfirmation,
  });

  useMultiSelectAnalytics(flowchartTypeName);

  return {
    interactiveNodes,
    interactiveEdges,
    setInteractiveNodes,
    applyFlowchartChanges,
    onReactFlowNodeChangesOnly,
    onReactFlowEdgeChangesOnly,
    onConnect,
    shouldAddEdgesForNewNode: shouldAddEdgesForNewNodeCallback,
    setNodesAndEdgesViaChangesForLayout,
    isLeftSidepanelOpen,
    interactiveFlowchartOptions,
    setPlaceholderNodeBeingAdded,
    getMeasuredDimensionsFromStore,
  };
};

export default useFlowchartState;
